import React from 'react';
import { Link } from 'react-router-dom';
import { createUseStyles } from 'react-jss';
import { S, A } from './tokens';

/** The widest an admin screen's content gets (it's centred in the space beside the sidebar). */
export const PAGE_MAX_WIDTH = 1920;

const useStyles = createUseStyles({
  page: {
    boxSizing: 'border-box',
    width: '100%',
    // Centred, using the whole width on laptops and 1920px monitors; capped so that on big
    // and ultra-wide monitors rows don't stretch across the screen.
    maxWidth: PAGE_MAX_WIDTH,
    margin: '0 auto',
    padding: '28px 32px 64px',
    fontFamily: S.ui,
    color: S.fg,
    '@media (max-width: 720px)': { padding: '20px 16px 48px' }
  },
  header: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 20
  },
  heading: { minWidth: 0, flex: '1 1 320px' },
  back: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    marginBottom: 6,
    fontSize: 13,
    fontWeight: 500,
    color: S.muted,
    textDecoration: 'none',
    '& .material-icons': { fontSize: 16 },
    '&:hover': { color: S.fg },
    '&:focus-visible': A.focus
  },
  title: { margin: 0, fontSize: 26, fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.01em' },
  description: { margin: '6px 0 0', fontSize: 14, lineHeight: 1.5, color: S.muted, maxWidth: 720 },
  actions: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 14
  },
  spacer: { flex: 1 },
  section: {
    marginTop: 28,
    '&:first-child': { marginTop: 0 }
  },
  sectionHead: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 10 },
  sectionTitle: { margin: 0, fontSize: 16, fontWeight: 600 },
  sectionHint: { fontSize: 13, color: S.muted },
  card: {
    background: A.surface,
    border: `1px solid ${S.line}`,
    borderRadius: 12,
    padding: '18px 20px'
  }
});

type PageProps = { children: React.ReactNode; className?: string; testId?: string; wide?: boolean };

/** The frame of an admin page: comfortable margins, readable width. */
export const Page: React.FC<PageProps> = ({ children, className, testId, wide }) => {
  const c = useStyles();
  return <main className={[c.page, className].filter(Boolean).join(' ')} style={wide ? { maxWidth: 'none' } : undefined} data-testid={testId}>{children}</main>;
};

type HeaderProps = {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Buttons on the right: one primary at most. */
  actions?: React.ReactNode;
  back?: { to: string; label: string };
};

/** Title, one line on what the page is for, and the page's main actions. */
export const PageHeader: React.FC<HeaderProps> = ({ title, description, actions, back }) => {
  const c = useStyles();
  return (
    <header className={c.header}>
      <div className={c.heading}>
        {back && (
          <Link to={back.to} className={c.back}>
            <i className="material-icons" aria-hidden>chevron_left</i>
            {back.label}
          </Link>
        )}
        <h1 className={c.title}>{title}</h1>
        {description && <p className={c.description}>{description}</p>}
      </div>
      {actions && <div className={c.actions}>{actions}</div>}
    </header>
  );
};

/** Filters and view controls above a list. */
export const Toolbar: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => {
  const c = useStyles();
  return <div className={[c.toolbar, className].filter(Boolean).join(' ')}>{children}</div>;
};

export const Spacer: React.FC = () => {
  const c = useStyles();
  return <span className={c.spacer} />;
};

type SectionProps = { title?: React.ReactNode; hint?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode; testId?: string; className?: string };

/** A titled part of a page. */
export const Section: React.FC<SectionProps> = ({ title, hint, actions, children, testId, className }) => {
  const c = useStyles();
  return (
    <section className={[c.section, className].filter(Boolean).join(' ')} data-testid={testId}>
      {(title || hint || actions) && (
        <div className={c.sectionHead}>
          <div>
            {title && <h2 className={c.sectionTitle}>{title}</h2>}
            {hint && <div className={c.sectionHint}>{hint}</div>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
};

/** A white panel on the page. */
export const Card: React.FC<{ children: React.ReactNode; className?: string; testId?: string; style?: React.CSSProperties }> = ({ children, className, testId, style }) => {
  const c = useStyles();
  return <div className={[c.card, className].filter(Boolean).join(' ')} data-testid={testId} style={style}>{children}</div>;
};
